import React, { useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { EditButton, Checkbox } from "../../Components/Buttons";
import { entityIdKeyMap } from "../../EntityKeys";
import { formatDate, formatTime } from "../../helperFunctions";

export type FieldDefinition = {
  readonly name: string;
  readonly label: string;
  readonly type?: string;
  readonly required?: boolean;
  readonly options?: { value: any; label: string }[];
};

interface UseTableColumnsProps {
  data: any[];
  category: string;
  fields?: readonly FieldDefinition[];
  selectedIds: number[];
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;
  handleRowClick?: (item: any) => void;
  /** For patientSurgery: count of documents linked to each surgery. */
  getRelatedDocumentCount?: (surgery: any) => number;
}

export const useTableColumns = ({
  data,
  category,
  fields = [],
  selectedIds,
  setSelectedIds,
  handleRowClick,
  getRelatedDocumentCount,
}: UseTableColumnsProps) => {
  const columns = useMemo<ColumnDef<any>[]>(() => {
    if (!data || data.length === 0) return [];

    const cols: ColumnDef<any>[] = [
      // 1. Checkbox Column
      {
        id: "select",
        header: () => null,
        cell: ({ row }) => {
          const item = row.original;
          const idKey =
            entityIdKeyMap[category as keyof typeof entityIdKeyMap] || "id";
          const rowId = item[idKey] || item.id || item._id || row.index;
          const isSelected = selectedIds.includes(rowId);

          return (
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                setSelectedIds((prev) =>
                  prev.includes(rowId)
                    ? prev.filter((id) => id !== rowId)
                    : [...prev, rowId],
                );
              }}
            />
          );
        },
      },
    ];

    // 2. Dynamic Data Columns
    if (fields && fields.length > 0) {
      fields.forEach((field) => {
        cols.push({
          id: field.name, // ✅ ΠΡΟΣΘΗΚΗ: Απαραίτητο ID για να το βρίσκει το printConfig
          header: field.label,
          accessorFn: (row) => {
            const rawValue = row[field.name];

            // Α) Αν είναι Select / Foreign Key
            if (field.type === "select") {
              const baseProp = field.name.replace(/Id$/i, "");
              const possibleDescrKeys = [
                `${baseProp}Descr`,
                `${baseProp}Description`,
                `${baseProp}Name`,
                `${field.name}Descr`,
                "descr",
                "description",
              ];

              for (const key of possibleDescrKeys) {
                if (row[key] !== undefined && row[key] !== null) {
                  return row[key];
                }
              }

              if (field.options) {
                const foundOpt = field.options.find(
                  (opt) => String(opt.value) === String(rawValue),
                );
                if (foundOpt) return foundOpt.label;
              }
            }
            if (
              field.type === "date" ||
              field.name.toLowerCase().includes("date")
            ) {
              return formatDate(rawValue);
            }

            if (
              field.type === "time" ||
              field.name.toLowerCase().includes("time")
            ) {
              return formatTime(rawValue);
            }
            // Β) Αν είναι Boolean
            if (typeof rawValue === "boolean") {
              return rawValue ? "Ναι" : "Όχι";
            }

            return rawValue;
          },
          cell: ({ getValue }) => {
            const val = getValue();
            return val !== undefined && val !== null && val !== ""
              ? String(val)
              : "-";
          },
        });
      });
    } else {
      // Fallback: Αν δεν περαστούν fields
      const dynamicKeys = Object.keys(data[0]).filter(
        (key) => !key.toLowerCase().includes("id") && key !== "__v",
      );

      dynamicKeys.forEach((key) => {
        cols.push({
          id: key, // ✅ ΠΡΟΣΘΗΚΗ ID
          accessorKey: key,
          header: key,
          cell: ({ getValue }) => {
            const val = getValue();
            if (typeof val === "boolean") return val ? "Ναι" : "Όχι";
            return val !== undefined && val !== null ? String(val) : "-";
          },
        });
      });
    }

    // 3. Correlation columns (Surgeries ↔ Documents)
    if (category === "patientSurgery" && getRelatedDocumentCount) {
      cols.push({
        id: "relatedDocuments",
        header: "Σχετικά Έγγραφα",
        accessorFn: (row) => getRelatedDocumentCount(row),
        cell: ({ getValue }) => {
          const count = Number(getValue()) || 0;
          return (
            <span
              className={`inline-flex min-w-[1.75rem] items-center justify-center rounded-md px-2 py-0.5 text-xs font-semibold ${
                count > 0
                  ? "bg-slate-100 text-slate-700"
                  : "bg-slate-50 text-slate-400"
              }`}
            >
              {count}
            </span>
          );
        },
      });
    }

    if (category === "patientDocument") {
      cols.push({
        id: "relatedSurgeries",
        header: "Σχετικά Χειρουργεία",
        accessorFn: (row) => {
          const surgeries = row.patientSurgery ?? [];
          if (!surgeries.length) return "-";
          return surgeries
            .map((s: any) => {
              const date = s.surgeryDate
                ? formatDate(s.surgeryDate)
                : "";
              const descr =
                s.surgeryDescription ?? s.surgeryTypeDescr ?? "Χειρουργείο";
              return date ? `${descr} (${date})` : descr;
            })
            .join(", ");
        },
        cell: ({ getValue }) => {
          const val = getValue();
          return val !== undefined && val !== null && val !== ""
            ? String(val)
            : "-";
        },
      });
    }

    // 4. Actions Column
    if (handleRowClick) {
      cols.push({
        id: "actions",
        header: () => <div className="text-right">Ενέργειες</div>,
        cell: ({ row }) => (
          <div className="flex justify-end">
            <EditButton
              onClick={(e) => {
                e.stopPropagation();
                handleRowClick(row.original);
              }}
            />
          </div>
        ),
      });
    }

    return cols;
  }, [
    data,
    category,
    fields,
    selectedIds,
    setSelectedIds,
    handleRowClick,
    getRelatedDocumentCount,
  ]);

  return columns;
};
